import { useRouter } from 'next/router';
import { useCallback } from 'react';

// URL-backed lens state for the insights pages. The dashboard renders the same
// District→School→Teacher→Class→Lesson hierarchy through one of two lenses:
//   • growth   — writing SCORES over time (the original dashboard)
//   • gaps     — TEKS standards mastery ("where gaps sit")
//   • students — the same skill gaps, one row per student (latest verdict per
//                skill), opening into a student's every lesson
// Same pattern as `useRubric`/`useTimePeriod` — switching replaces the URL via
// shallow routing so back/forward step through lens changes, and drilling
// carries the active lens via `querySuffix()` (which forwards `?lens=gaps`).
//
// The Gaps lens needs the viewer's gaps flag, and the Students (Skill gaps)
// lens needs `skill_gaps_v2` on top of it (both resolved by
// `useInsightsAccess`). A lens the viewer lacks is ignored and falls back to
// growth, so a shared link can't surface it.
export type InsightsLens = 'growth' | 'gaps' | 'students';

// The lenses behind the gaps flag — the ones a link may carry only when the
// viewer holds it. One list, read by useLens and by useTimePeriod's link suffix.
export const GAPS_LENSES: readonly InsightsLens[] = ['gaps', 'students'];

export const isGapsLens = (v: unknown): v is 'gaps' | 'students' =>
  typeof v === 'string' && (GAPS_LENSES as readonly string[]).includes(v);

// Whether this viewer may land on a requested gaps lens.
export const isAllowedGapsLens = (
  v: unknown,
  access: { gapsLensOn: boolean; skillGapsOn: boolean },
): v is 'gaps' | 'students' =>
  isGapsLens(v) && (v === 'students' ? access.skillGapsOn : access.gapsLensOn);

export const DEFAULT_LENS: InsightsLens = 'growth';

export function useLens(
  gapsLensOn: boolean,
  skillGapsOn: boolean,
): {
  lens: InsightsLens;
  setLens: (next: InsightsLens) => void;
} {
  const router = useRouter();
  const requested = router.query.lens;
  const lens: InsightsLens = isAllowedGapsLens(requested, { gapsLensOn, skillGapsOn })
    ? requested
    : 'growth';

  const setLens = useCallback(
    (next: InsightsLens) => {
      const query: Record<string, string | string[] | undefined> = {
        ...router.query,
      };
      if (next === DEFAULT_LENS) delete query.lens;
      else query.lens = next;
      router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
        scroll: false,
      });
    },
    [router],
  );

  return { lens, setLens };
}
